import { useState } from 'react';
import { DeleteOutlined, PictureOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  App,
  Button,
  Card,
  DatePicker,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import type { NewsAdmin, RichText } from '@titularis/api-client';
import { api } from '../api';
import { EditorTexto } from '../components/EditorTexto';
import { formatarData } from '../dominio';

/**
 * Notícias Recentes (PR-35).
 *
 * O editor é o **mesmo** das questões, e não um parecido: o formato canônico
 * é o JSON do TipTap nos dois casos, e uma segunda lista de extensões seria
 * uma segunda lista para divergir da do servidor. Formatação que divergisse
 * aqui sumiria na página publicada, sem erro nenhum no caminho.
 *
 * A tela não tem "publicar" como botão separado. **A data é a publicação**:
 * vazia deixa em rascunho, futura agenda, passada põe no ar. Um botão ao lado
 * de um campo de data seria dois jeitos de dizer a mesma coisa, e o dia em que
 * discordassem alguém teria de decidir qual manda.
 */

/** Os três estados que a data descreve, na ordem em que a redação os vive. */
function situacao(publishedAt: string | null): { label: string; cor: string } {
  if (!publishedAt) return { label: 'Rascunho', cor: 'default' };
  return new Date(publishedAt) > new Date()
    ? { label: 'Agendada', cor: 'blue' }
    : { label: 'No ar', cor: 'green' };
}

export function NoticiasPage() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [emFormulario, setEmFormulario] = useState<NewsAdmin | null | undefined>(undefined);

  const noticias = useQuery({
    queryKey: ['news', 'drafts'],
    queryFn: () => api.listNewsDrafts(),
  });

  const apagar = useMutation({
    mutationFn: (id: string) => api.deleteNews(id),
    onSuccess: async () => {
      message.success('Notícia apagada.');
      await queryClient.invalidateQueries({ queryKey: ['news'] });
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  const itens = noticias.data?.items ?? [];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space style={{ width: '100%', justifyContent: 'space-between' }} align="start">
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>
            Notícias Recentes
          </Typography.Title>
          <Typography.Text type="secondary">
            Públicas e sem login, em titularis.com.br/noticias. Rascunho e agendada não aparecem
            para ninguém de fora.
          </Typography.Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setEmFormulario(null)}>
          Nova notícia
        </Button>
      </Space>

      <Card styles={{ body: { padding: 0 } }}>
        <Table
          rowKey="id"
          loading={noticias.isLoading}
          dataSource={itens}
          pagination={false}
          locale={{
            emptyText: <Empty description="Nenhuma notícia ainda. A primeira abre a seção." />,
          }}
          columns={[
            {
              title: 'Título',
              dataIndex: 'title',
              render: (titulo: string, linha: NewsAdmin) => (
                <Space direction="vertical" size={0}>
                  <Typography.Link onClick={() => setEmFormulario(linha)}>{titulo}</Typography.Link>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    /noticias/{linha.slug}
                  </Typography.Text>
                </Space>
              ),
            },
            {
              title: 'Situação',
              dataIndex: 'publishedAt',
              width: 130,
              render: (valor: string | null) => {
                const { label, cor } = situacao(valor);
                return <Tag color={cor}>{label}</Tag>;
              },
            },
            {
              title: 'Publicação',
              dataIndex: 'publishedAt',
              width: 160,
              render: (valor: string | null) => formatarData(valor),
            },
            { title: 'Autor', dataIndex: 'authorName', width: 180, render: (v: string) => v ?? '-' },
            {
              title: 'Capa',
              dataIndex: 'temImagem',
              width: 80,
              align: 'center' as const,
              render: (tem: boolean) =>
                tem ? (
                  <Tooltip title="Tem capa">
                    <PictureOutlined />
                  </Tooltip>
                ) : (
                  <Typography.Text type="secondary">-</Typography.Text>
                ),
            },
            {
              title: '',
              width: 60,
              render: (_: unknown, linha: NewsAdmin) => (
                <Popconfirm
                  title="Apagar esta notícia?"
                  description="A capa vai junto. Não tem desfazer."
                  okText="Apagar"
                  cancelText="Cancelar"
                  onConfirm={() => apagar.mutate(linha.id)}
                >
                  <Button type="text" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              ),
            },
          ]}
        />
      </Card>

      {emFormulario !== undefined && (
        <FormularioDeNoticia
          noticia={emFormulario}
          onFechar={() => setEmFormulario(undefined)}
        />
      )}
    </Space>
  );
}

/**
 * O formulário. A capa só entra depois de a notícia existir, porque o caminho
 * do arquivo no bucket carrega o id - e pedir o id antes de haver linha
 * significaria criar a linha escondido do editor, que é pior.
 */
function FormularioDeNoticia({
  noticia,
  onFechar,
}: {
  noticia: NewsAdmin | null;
  onFechar: () => void;
}) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();

  const [salva, setSalva] = useState<NewsAdmin | null>(noticia);
  const [doc, setDoc] = useState<RichText | null>(noticia?.doc ?? null);

  const salvar = useMutation({
    mutationFn: (valores: { title: string; publishedAt?: Dayjs | null }) => {
      const corpo = {
        title: valores.title,
        doc: doc!,
        publishedAt: valores.publishedAt ? valores.publishedAt.toISOString() : null,
      };
      return salva ? api.updateNews(salva.id, corpo) : api.createNews(corpo);
    },
    onSuccess: async (dados) => {
      await queryClient.invalidateQueries({ queryKey: ['news'] });
      setSalva(dados);
      message.success(dados.publishedAt ? 'Notícia salva.' : 'Rascunho salvo.');
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  const enviarCapa = useMutation({
    mutationFn: (arquivo: File) => api.uploadNewsImage(salva!.id, arquivo),
    onSuccess: async (dados) => {
      message.success('Capa enviada.');
      setSalva(dados);
      await queryClient.invalidateQueries({ queryKey: ['news'] });
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  return (
    <Modal
      open
      width={760}
      title={noticia ? 'Editar notícia' : 'Nova notícia'}
      onCancel={onFechar}
      footer={[
        <Button key="fechar" onClick={onFechar}>
          Fechar
        </Button>,
        <Button
          key="salvar"
          type="primary"
          loading={salvar.isPending}
          onClick={() => {
            // O corpo vazio é recusado pelo servidor de qualquer forma; avisar
            // aqui evita a ida ao servidor para ouvir o que já se sabe.
            if (!doc) {
              message.error('A notícia precisa de um texto.');
              return;
            }
            void form.validateFields().then((valores) => salvar.mutate(valores));
          }}
        >
          Salvar
        </Button>,
      ]}
      afterOpenChange={(abriu) => {
        if (abriu) {
          form.setFieldsValue({
            title: noticia?.title,
            publishedAt: noticia?.publishedAt ? dayjs(noticia.publishedAt) : null,
          });
        }
      }}
      destroyOnHidden
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="title"
          label="Título"
          rules={[{ required: true, message: 'A notícia precisa de um título.' }]}
          extra={
            salva
              ? `Endereço: /noticias/${salva.slug}. Muda se o título mudar.`
              : 'O endereço da página sai daqui.'
          }
        >
          <Input maxLength={200} placeholder="Provimento nº 190 do CNJ muda a indisponibilidade" />
        </Form.Item>

        <Form.Item label="Texto" required>
          <EditorTexto value={doc} onChange={setDoc} minHeight={220} />
        </Form.Item>

        <Form.Item
          name="publishedAt"
          label="Publicação"
          extra="Vazio deixa em rascunho. Data no futuro agenda: a notícia aparece sozinha na hora marcada."
        >
          <DatePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: 260 }} />
        </Form.Item>

        <Form.Item
          label="Capa"
          extra={
            salva
              ? 'JPEG, PNG ou WebP, até 5 MB. Trocar a capa substitui a anterior.'
              : 'Disponível depois de salvar pela primeira vez.'
          }
        >
          <Space>
            <Upload
              accept="image/jpeg,image/png,image/webp"
              showUploadList={false}
              disabled={!salva}
              beforeUpload={(arquivo) => {
                enviarCapa.mutate(arquivo as unknown as File);
                return false;
              }}
            >
              <Button icon={<PictureOutlined />} disabled={!salva} loading={enviarCapa.isPending}>
                {salva?.temImagem ? 'Trocar capa' : 'Enviar capa'}
              </Button>
            </Upload>
            {salva?.temImagem && <Tag color="green">Capa enviada</Tag>}
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
}
